import { useState, useEffect, useRef, useMemo } from 'react';
import { auth, db, storage } from '../../services/firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';
import { ensureUploadableImage } from '../../utils/imageConversion';

type JobStatus =
  | 'idle'
  | 'uploading'
  | 'creating-job'
  | 'queued'
  | 'running'
  | 'done'
  | 'error';

interface AnalysisJob {
  status: string;
  error?: string | { code?: string; message?: string };
  result?: Record<string, unknown>;
  storagePathPreview?: string;
  storagePathThumb?: string;
  cancelRequested?: boolean;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function getFunctionUrl(functionName: string): string {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  return `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older Safari / non-HTTPS contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function fallbackCopy(text: string) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
  toast.success('Copied to clipboard');
}

export function ImageAnalysisTest() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const assetId = useMemo(() => generateId(), []);

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [enableBing, setEnableBing] = useState(false);

  // Conversion state
  const [isConverting, setIsConverting] = useState(false);

  // Upload state
  const [uploadProgress, setUploadProgress] = useState(0);

  // Job state
  const [jobStatus, setJobStatus] = useState<JobStatus>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobData, setJobData] = useState<AnalysisJob | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Image URLs from storage
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  // Create local preview when file is selected
  useEffect(() => {
    if (!selectedFile) {
      setLocalPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  // Subscribe to job document when jobId is set
  useEffect(() => {
    if (!jobId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'analysisJobs', jobId),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() as AnalysisJob;
        setJobData(data);

        if (data.status === 'queued') setJobStatus('queued');
        else if (data.status === 'running') setJobStatus('running');
        else if (data.status === 'done') setJobStatus('done');
        else if (data.status === 'error') {
          setJobStatus('error');
          const err = data.error;
          if (!err) setErrorMessage('Unknown error');
          else if (typeof err === 'string') setErrorMessage(err);
          else setErrorMessage(err.message || err.code || JSON.stringify(err));
        }
      },
      (err) => {
        console.error('Job snapshot error:', err);
        setErrorMessage(`Snapshot error: ${err.message}`);
      },
    );

    return () => unsubscribe();
  }, [jobId]);

  // Load preview/thumb images when job data updates with storage paths
  useEffect(() => {
    if (!jobData) return;

    // Check top-level job fields first, then fall back to result.image paths
    const result = jobData.result as Record<string, unknown> | undefined;
    const imageBlock = result?.image as Record<string, string> | undefined;
    const pathPreview = jobData.storagePathPreview || imageBlock?.storagePathPreview;
    const pathThumb = jobData.storagePathThumb || imageBlock?.storagePathThumb;

    if (pathPreview && !previewUrl) {
      getDownloadURL(ref(storage, pathPreview))
        .then(setPreviewUrl)
        .catch((err) => console.error('Failed to load preview:', err));
    }
    if (pathThumb && !thumbUrl) {
      getDownloadURL(ref(storage, pathThumb))
        .then(setThumbUrl)
        .catch((err) => console.error('Failed to load thumb:', err));
    }
  }, [jobData, previewUrl, thumbUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Reset previous job state
      setJobId(null);
      setJobData(null);
      setJobStatus('idle');
      setErrorMessage(null);
      setUploadProgress(0);
      setPreviewUrl(null);
      setThumbUrl(null);
    }
  };

  const handleUploadAndAnalyze = async () => {
    if (!selectedFile) return;

    const user = auth.currentUser;
    if (!user) {
      setErrorMessage('Not authenticated');
      return;
    }

    setErrorMessage(null);

    // 0. Convert to JPEG if the picked file isn't a type Storage will accept
    // (HEIC/HEIF, AVIF, BMP, TIFF…).
    let fileToUpload: File;
    try {
      setIsConverting(true);
      fileToUpload = await ensureUploadableImage(selectedFile);
      setIsConverting(false);
    } catch (err) {
      setIsConverting(false);
      setJobStatus('error');
      setErrorMessage(
        `Image conversion failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      return;
    }

    if (!fileToUpload.type.startsWith('image/')) {
      setJobStatus('error');
      setErrorMessage('Selected file is not an image.');
      return;
    }

    // 1. Upload to Firebase Storage
    setJobStatus('uploading');
    setUploadProgress(0);

    const now = new Date();
    const yyyy = now.getFullYear().toString();
    const mm = pad2(now.getMonth() + 1);
    const dd = pad2(now.getDate());
    const storagePathOriginal = `users/${user.uid}/uploads/${yyyy}/${mm}/${dd}/${assetId}/original.jpg`;

    try {
      const storageRef = ref(storage, storagePathOriginal);
      const uploadTask = uploadBytesResumable(storageRef, fileToUpload, {
        contentType: fileToUpload.type,
      });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(Math.round(progress));
          },
          (error) => reject(error),
          () => resolve(),
        );
      });
    } catch (err) {
      setJobStatus('error');
      setErrorMessage(
        `Upload failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      return;
    }

    // 2. Call createAnalysisJob Cloud Function
    setJobStatus('creating-job');

    try {
      const token = await user.getIdToken();
      const providers: string[] = ['vision'];
      if (enableBing) providers.push('bing');

      const response = await fetch(getFunctionUrl('createAnalysisJob'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assetId,
          storagePathOriginal,
          providersRequested: providers,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const result = await response.json();
      const newJobId = result.jobId;
      if (!newJobId) throw new Error('No jobId in response');

      setJobId(newJobId);
      setJobStatus('queued');
    } catch (err) {
      setJobStatus('error');
      setErrorMessage(
        `Create job failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const handleCancel = async () => {
    if (!jobId) return;
    try {
      await updateDoc(doc(db, 'analysisJobs', jobId), {
        cancelRequested: true,
      });
    } catch (err) {
      console.error('Cancel failed:', err);
      setErrorMessage(
        `Cancel failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const handleRefreshImages = () => {
    setPreviewUrl(null);
    setThumbUrl(null);
  };

  const isProcessing =
    isConverting ||
    jobStatus === 'uploading' ||
    jobStatus === 'creating-job' ||
    jobStatus === 'queued' ||
    jobStatus === 'running';
  const canCancel = jobStatus === 'queued' || jobStatus === 'running';

  // Extract top candidate from result
  const topCandidate = jobData?.result
    ? extractTopCandidate(jobData.result)
    : null;

  // Extract visually similar images from result
  const visuallySimilarImages = jobData?.result
    ? extractVisuallySimilarImages(jobData.result)
    : [];

  // Extract web signals
  const webSignals = jobData?.result
    ? extractWebSignals(jobData.result)
    : null;

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-semibold text-gray-900'>
          Image Analysis Test
        </h2>
        <p className='text-sm text-gray-500 mt-1'>
          Upload a watch image and run vision analysis. Internal testing tool.
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column - Input & Upload */}
        <div className='lg:col-span-1 space-y-4'>
          {/* File Picker */}
          <div className='bg-white shadow rounded-lg p-4'>
            <h3 className='font-medium text-gray-900 mb-3'>Upload Image</h3>

            <div
              className='border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-brand-400 transition-colors'
              onClick={() => fileInputRef.current?.click()}
            >
              {localPreviewUrl ? (
                <img
                  src={localPreviewUrl}
                  alt='Selected'
                  className='max-h-48 mx-auto rounded'
                />
              ) : (
                <div className='text-gray-400'>
                  <svg
                    className='w-12 h-12 mx-auto mb-2'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={1.5}
                      d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                    />
                  </svg>
                  <p className='text-sm'>Click to select an image</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type='file'
              accept='image/*'
              onChange={handleFileSelect}
              className='hidden'
            />

            {selectedFile && (
              <p className='text-xs text-gray-500 mt-2 truncate'>
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}

            <label className='flex items-center gap-2 cursor-pointer mt-3'>
              <input
                type='checkbox'
                checked={enableBing}
                onChange={(e) => setEnableBing(e.target.checked)}
                className='rounded text-brand-600 focus:ring-brand-500'
              />
              <span className='text-sm text-gray-700'>
                Enable Web Detection
              </span>
            </label>

            <Button
              className='w-full mt-3'
              onClick={handleUploadAndAnalyze}
              disabled={!selectedFile || isProcessing}
              loading={isProcessing}
            >
              Upload & Analyze
            </Button>
          </div>

          {/* Status Section */}
          <div className='bg-white shadow rounded-lg p-4'>
            <h3 className='font-medium text-gray-900 mb-3'>Status</h3>

            {/* HEIC Conversion */}
            {isConverting && (
              <div className='mb-3 flex items-center gap-2 text-sm text-blue-700'>
                <svg
                  className='w-4 h-4 animate-spin'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
                Converting HEIC to JPEG...
              </div>
            )}

            {/* Upload Progress */}
            {jobStatus === 'uploading' && (
              <div className='mb-3'>
                <div className='flex justify-between text-xs text-gray-500 mb-1'>
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className='w-full bg-gray-200 rounded-full h-2'>
                  <div
                    className='bg-brand-600 h-2 rounded-full transition-all'
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className='space-y-2 text-sm'>
              <div className='flex justify-between'>
                <span className='text-gray-500'>Status:</span>
                <StatusBadge status={jobStatus} />
              </div>
              {jobId && (
                <div className='flex justify-between'>
                  <span className='text-gray-500'>Job ID:</span>
                  <span className='text-gray-900 font-mono text-xs truncate ml-2 max-w-[180px]'>
                    {jobId}
                  </span>
                </div>
              )}
              <div className='flex justify-between'>
                <span className='text-gray-500'>Asset ID:</span>
                <span className='text-gray-900 font-mono text-xs truncate ml-2 max-w-[180px]'>
                  {assetId}
                </span>
              </div>
            </div>

            {errorMessage && (
              <div className='mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 break-words'>
                {errorMessage}
              </div>
            )}

            {canCancel && (
              <Button
                variant='danger'
                size='sm'
                className='w-full mt-3'
                onClick={handleCancel}
              >
                Cancel Job
              </Button>
            )}
          </div>
        </div>

        {/* Right Column - Results */}
        <div className='lg:col-span-2 space-y-4'>
          {/* Preview Images */}
          {(previewUrl || thumbUrl) && (
            <div className='bg-white shadow rounded-lg p-4'>
              <div className='flex items-center justify-between mb-3'>
                <h3 className='font-medium text-gray-900'>Generated Images</h3>
                <button
                  onClick={handleRefreshImages}
                  className='text-sm text-brand-600 hover:text-brand-700'
                >
                  Refresh
                </button>
              </div>
              <div className='flex gap-4'>
                {previewUrl && (
                  <div>
                    <p className='text-xs text-gray-500 mb-1'>preview.jpg</p>
                    <img
                      src={previewUrl}
                      alt='Preview'
                      className='max-h-40 rounded border'
                    />
                  </div>
                )}
                {thumbUrl && (
                  <div>
                    <p className='text-xs text-gray-500 mb-1'>thumb.jpg</p>
                    <img
                      src={thumbUrl}
                      alt='Thumbnail'
                      className='max-h-40 rounded border'
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Top Candidate */}
          {topCandidate && (
            <div className='bg-white shadow rounded-lg p-4'>
              <h3 className='font-medium text-gray-900 mb-3'>Top Candidate</h3>
              <div className='space-y-2'>
                {topCandidate.title && (
                  <div className='flex gap-2'>
                    <span className='text-sm text-gray-500 w-24 shrink-0'>
                      Title:
                    </span>
                    <span className='text-sm font-medium text-gray-900'>
                      {topCandidate.title}
                    </span>
                  </div>
                )}
                {topCandidate.reference && (
                  <div className='flex gap-2'>
                    <span className='text-sm text-gray-500 w-24 shrink-0'>
                      Reference:
                    </span>
                    <span className='text-sm font-mono text-gray-900'>
                      {topCandidate.reference}
                    </span>
                  </div>
                )}
                {topCandidate.confidence != null && (
                  <div className='flex gap-2'>
                    <span className='text-sm text-gray-500 w-24 shrink-0'>
                      Confidence:
                    </span>
                    <ConfidenceBadge value={topCandidate.confidence} />
                  </div>
                )}
                {topCandidate.reasons && topCandidate.reasons.length > 0 && (
                  <div>
                    <span className='text-sm text-gray-500'>Reasons:</span>
                    <ul className='mt-1 list-disc list-inside text-sm text-gray-700 space-y-0.5'>
                      {topCandidate.reasons.map((r, i) => (
                        <li key={i}>
                          {typeof r === 'string'
                            ? r
                            : r.detail || JSON.stringify(r)}
                          {typeof r === 'object' && r.weight != null && (
                            <span className='ml-1 text-xs text-gray-400'>
                              (weight: {r.weight})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Visually Similar Images */}
          {visuallySimilarImages.length > 0 && (
            <div className='bg-white shadow rounded-lg p-4'>
              <h3 className='font-medium text-gray-900 mb-3'>
                Visually Similar Images
                <span className='ml-2 text-xs font-normal text-gray-400'>
                  ({visuallySimilarImages.length})
                </span>
              </h3>
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'>
                {visuallySimilarImages.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='block group'
                  >
                    <div className='aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 group-hover:border-brand-400 transition-colors'>
                      <img
                        src={url}
                        alt={`Similar ${i + 1}`}
                        className='w-full h-full object-cover'
                        loading='lazy'
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML =
                            '<div class="w-full h-full flex items-center justify-center text-xs text-gray-400 p-2 text-center">Failed to load</div>';
                        }}
                      />
                    </div>
                    <p className='text-xs text-gray-500 mt-1 truncate group-hover:text-brand-600'>
                      {new URL(url).hostname}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Web Matches */}
          {webSignals &&
            (webSignals.pages.length > 0 ||
              webSignals.entities.length > 0) && (
              <div className='bg-white shadow rounded-lg p-4'>
                <h3 className='font-medium text-gray-900 mb-3'>Web Matches</h3>

                {webSignals.entities.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-xs text-gray-500 mb-2'>Entities</p>
                    <div className='flex flex-wrap gap-1.5'>
                      {webSignals.entities.map((e, i) => (
                        <span
                          key={i}
                          className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-brand-50 text-brand-700 border border-brand-200'
                        >
                          {e.name}
                          <span className='text-brand-400'>
                            {(e.confidence * 100).toFixed(0)}%
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {webSignals.bestGuessLabels.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-xs text-gray-500 mb-1'>Best Guess</p>
                    <p className='text-sm font-medium text-gray-900'>
                      {webSignals.bestGuessLabels.join(', ')}
                    </p>
                  </div>
                )}

                {webSignals.pages.length > 0 && (
                  <div>
                    <p className='text-xs text-gray-500 mb-2'>
                      Matching Pages ({webSignals.pages.length})
                    </p>
                    <div className='space-y-2'>
                      {webSignals.pages.map((page, i) => (
                        <a
                          key={i}
                          href={page.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='block p-2 rounded border border-gray-200 hover:border-brand-400 hover:bg-brand-50 transition-colors'
                        >
                          <p className='text-sm text-brand-700 truncate'>
                            {page.title || page.url}
                          </p>
                          <p className='text-xs text-gray-400 truncate mt-0.5'>
                            {page.url}
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

          )}

          {/* Result JSON */}
          {jobData?.result && (
            <div className='bg-white shadow rounded-lg p-4'>
              <div className='flex items-center justify-between mb-3'>
                <h3 className='font-medium text-gray-900'>Result JSON</h3>
                <button
                  onClick={() => {
                    const text = JSON.stringify(jobData.result, null, 2);
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(text).then(
                        () => toast.success('Copied to clipboard'),
                        () => fallbackCopy(text),
                      );
                    } else {
                      fallbackCopy(text);
                    }
                  }}
                  className='text-sm text-brand-600 hover:text-brand-700'
                >
                  Copy
                </button>
              </div>
              <pre className='bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-[50vh] text-xs leading-relaxed'>
                {JSON.stringify(jobData.result, null, 2)}
              </pre>
            </div>
          )}

          {/* Empty state */}
          {jobStatus === 'idle' && (
            <div className='bg-white shadow rounded-lg p-8 text-center text-gray-400'>
              <svg
                className='w-16 h-16 mx-auto mb-3'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1}
                  d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
                />
              </svg>
              <p className='text-sm'>
                Select an image and click "Upload & Analyze" to begin.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -- Helper components --

function StatusBadge({ status }: { status: JobStatus }) {
  const config: Record<JobStatus, { label: string; className: string }> = {
    idle: { label: 'Idle', className: 'bg-gray-100 text-gray-600' },
    uploading: { label: 'Uploading', className: 'bg-blue-100 text-blue-700' },
    'creating-job': {
      label: 'Creating Job',
      className: 'bg-blue-100 text-blue-700',
    },
    queued: { label: 'Queued', className: 'bg-yellow-100 text-yellow-700' },
    running: { label: 'Running', className: 'bg-purple-100 text-purple-700' },
    done: { label: 'Done', className: 'bg-green-100 text-green-700' },
    error: { label: 'Error', className: 'bg-red-100 text-red-700' },
  };
  const c = config[status];
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.className}`}
    >
      {c.label}
    </span>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  let color = 'bg-red-100 text-red-700';
  if (value >= 0.8) color = 'bg-green-100 text-green-700';
  else if (value >= 0.5) color = 'bg-yellow-100 text-yellow-700';

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {(value * 100).toFixed(1)}%
    </span>
  );
}

interface Reason {
  detail?: string;
  weight?: number;
  type?: string;
}

interface TopCandidate {
  title?: string;
  reference?: string;
  confidence?: number;
  reasons?: (string | Reason)[];
}

interface WebPage {
  url: string;
  title?: string;
}

interface WebEntity {
  name: string;
  confidence: number;
}

interface WebSignals {
  pages: WebPage[];
  entities: WebEntity[];
  bestGuessLabels: string[];
}

function extractWebSignals(result: Record<string, unknown>): WebSignals | null {
  const signals = result.signals as Record<string, unknown> | undefined;
  const web = signals?.web as Record<string, unknown> | undefined;
  if (!web) return null;

  const pages = Array.isArray(web.pages)
    ? (web.pages as WebPage[]).filter((p) => p.url)
    : [];
  const entities = Array.isArray(web.entities)
    ? (web.entities as WebEntity[]).filter((e) => e.name)
    : [];
  const bestGuessLabels = Array.isArray(web.bestGuessLabels)
    ? (web.bestGuessLabels as string[])
    : [];

  if (pages.length === 0 && entities.length === 0 && bestGuessLabels.length === 0) {
    return null;
  }

  return { pages, entities, bestGuessLabels };
}

function extractVisuallySimilarImages(result: Record<string, unknown>): string[] {
  // Primary: result.signals.web.visuallySimilarImages[].url
  const signals = result.signals as Record<string, unknown> | undefined;
  const web = signals?.web as Record<string, unknown> | undefined;
  const vsImages = web?.visuallySimilarImages as Array<{ url?: string }> | undefined;
  if (Array.isArray(vsImages) && vsImages.length > 0) {
    return vsImages.map((img) => img.url).filter((u): u is string => !!u);
  }
  return [];
}

function extractTopCandidate(
  result: Record<string, unknown>,
): TopCandidate | null {
  // Try common result shapes
  // Shape 1: result.topCandidate
  if (result.topCandidate && typeof result.topCandidate === 'object') {
    return result.topCandidate as TopCandidate;
  }
  // Shape 2: result.candidates[0]
  if (Array.isArray(result.candidates) && result.candidates.length > 0) {
    return result.candidates[0] as TopCandidate;
  }
  // Shape 3: result.vision.topCandidate
  if (result.vision && typeof result.vision === 'object') {
    const vision = result.vision as Record<string, unknown>;
    if (vision.topCandidate && typeof vision.topCandidate === 'object') {
      return vision.topCandidate as TopCandidate;
    }
    if (Array.isArray(vision.candidates) && vision.candidates.length > 0) {
      return vision.candidates[0] as TopCandidate;
    }
  }
  return null;
}
